import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { bloomberg } from '../theme/bloombergTheme';
import { formatMacroValue } from '../utils/macroFormat';
import type { MacroSeriesResponse } from '../services/api';

interface MacroSeriesChartProps {
  series: MacroSeriesResponse;
  height?: number;
}

const MONO = bloomberg.font.mono;

/**
 * One macro series over time, with NBER recession shading.
 *
 * Single series by design. os-bloom pairs each row with an overlay series on a
 * second scale; that is a dual-axis chart, where the alignment of the two
 * scales is arbitrary and invents a correlation the data does not contain. If
 * two series need comparing here, they get two charts.
 */
const MacroSeriesChart: React.FC<MacroSeriesChartProps> = ({ series, height = 300 }) => {
  const data = useMemo(
    () => series.points.map((p) => ({ t: Date.parse(p.d), v: p.v })),
    [series.points]
  );

  const domain = useMemo<[number, number] | null>(() => {
    if (data.length === 0) return null;
    return [data[0].t, data[data.length - 1].t];
  }, [data]);

  // Clip bands to the visible window so a recession that starts before the
  // chart does not stretch the axis backwards.
  const bands = useMemo(() => {
    if (!domain) return [];
    return (series.recession_bands || [])
      .map((b) => ({ x1: Date.parse(b.start), x2: Date.parse(b.end) }))
      .filter((b) => b.x2 >= domain[0] && b.x1 <= domain[1])
      .map((b) => ({ x1: Math.max(b.x1, domain[0]), x2: Math.min(b.x2, domain[1]) }));
  }, [series.recession_bands, domain]);

  // Spreads, curves and diffusion indices are read against zero; a hairline
  // there is the difference between "inverted" and "flat" being legible.
  const showZeroLine = useMemo(() => {
    if (data.length === 0) return false;
    const lo = Math.min(...data.map((d) => d.v));
    const hi = Math.max(...data.map((d) => d.v));
    return lo < 0 && hi > 0;
  }, [data]);

  const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as { t: number; v: number };
    return (
      <Box
        sx={{
          bgcolor: bloomberg.bg.tertiary,
          border: `1px solid ${bloomberg.border.secondary}`,
          borderRadius: 1,
          px: 1.25,
          py: 0.75,
        }}
      >
        <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.muted }}>
          {new Date(p.t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.bodyDense, color: bloomberg.text.primary, fontWeight: 600 }}>
          {formatMacroValue(p.v, series.unit)}
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: bloomberg.text.muted, fontSize: bloomberg.fontSize.body }}>
          No data in this window
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
          {/* Recession shading sits behind everything, in neutral ink — it is
              context, not a series, so it never takes a hue. */}
          {bands.map((b) => (
            <ReferenceArea
              key={b.x1}
              x1={b.x1}
              x2={b.x2}
              fill={bloomberg.text.muted}
              fillOpacity={0.13}
              strokeOpacity={0}
            />
          ))}
          <CartesianGrid stroke={bloomberg.chart.grid} strokeWidth={1} vertical={false} />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={domain || ['dataMin', 'dataMax']}
            tickFormatter={(t) => new Date(t).getFullYear().toString()}
            minTickGap={44}
            tick={{ fill: bloomberg.text.muted, fontSize: bloomberg.fontSize.micro, fontFamily: MONO }}
            stroke={bloomberg.chart.axis}
          />
          <YAxis
            width={58}
            tickFormatter={(v) => formatMacroValue(v, series.unit)}
            tick={{ fill: bloomberg.text.muted, fontSize: bloomberg.fontSize.micro, fontFamily: MONO }}
            stroke={bloomberg.chart.axis}
          />
          {showZeroLine && (
            <ReferenceLine y={0} stroke={bloomberg.text.muted} strokeWidth={1} strokeOpacity={0.55} />
          )}
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: bloomberg.text.muted, strokeWidth: 1 }} />
          <Line
            type="monotone"
            dataKey="v"
            stroke={bloomberg.accent.orange}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: bloomberg.bg.primary }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default MacroSeriesChart;
